/**
 * Calendar utility helpers shared across night-shift declaration components.
 *
 * Provides the {@link buildCalendarGrid} function together with the
 * {@link DayEntry} / {@link WeekEntry} interfaces and the {@link DAY_LABELS}
 * constant so that both `ApplyNightShiftDeclaration` and
 * `NightShiftDeclarationList` can reuse the same grid-building logic without
 * duplication.
 *
 * @module night-shift-declarations/calendarUtils
 */

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

/**
 * Represents a single day within the calendar grid.
 */
export interface DayEntry {
    /** ISO date string (YYYY-MM-DD). */
    date: string;
    /** Day of month (1–31). */
    day: number;
    /** Mon-based day-of-week index (0 = Monday, 6 = Sunday). */
    dayOfWeek: number;
    /** Whether the day falls before today. */
    isPast: boolean;
    /** Whether an active (non-cancelled) declaration already exists for this date. */
    isAlreadyDeclared: boolean;
}

/**
 * Represents a single week row within the calendar grid.
 */
export interface WeekEntry {
    /**
     * Array of exactly 7 slots.  Slots before the first day of the month and
     * after the last day are `null` (padding).
     */
    days: (DayEntry | null)[];
    /** Zero-based week index within the month. */
    weekIndex: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Abbreviated day-of-week labels, starting from Monday (Mon-based grid).
 */
export const DAY_LABELS: readonly string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Builds the calendar grid for a given year / month combination.
 *
 * The grid uses a **Monday-first** layout: Sunday is placed in the rightmost
 * (index 6) column.  The first and last weeks are padded with `null` entries
 * so that every {@link WeekEntry} contains exactly 7 slots.
 *
 * @param year         - Full four-digit year (e.g. 2025).
 * @param month        - Zero-based month index (0 = January … 11 = December).
 * @param existingDates - Set of ISO date strings (`YYYY-MM-DD`) for which an
 *                        active declaration already exists.  Days present in
 *                        this set are flagged as {@link DayEntry.isAlreadyDeclared}.
 * @returns An ordered array of {@link WeekEntry} objects covering the entire month.
 *
 * @example
 * const grid = buildCalendarGrid(2025, 2, new Set(['2025-03-10']));
 * // grid[0].days contains the first week of March 2025
 */
export function buildCalendarGrid (year: number, month: number, existingDates: Set<string>): WeekEntry[] {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks: WeekEntry[] = [];
    let currentWeek: (DayEntry | null)[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        // Convert JS dayOfWeek (0 = Sun) to Mon-based (0 = Mon, 6 = Sun)
        const jsDow = date.getDay();
        const monBasedDow = jsDow === 0 ? 6 : jsDow - 1;

        // Pad the first week with nulls for days before the month starts
        if (day === 1 && monBasedDow > 0) {
            for (let pad = 0; pad < monBasedDow; pad++) {
                currentWeek.push(null);
            }
        }

        const entry: DayEntry = {
            date: dateStr,
            day,
            dayOfWeek: monBasedDow,
            isPast: dateStr < today,
            isAlreadyDeclared: existingDates.has(dateStr),
        };

        currentWeek.push(entry);

        // If we've reached Sunday (index 6) or the last day of the month, commit the week
        if (monBasedDow === 6 || day === daysInMonth) {
            // Pad the end of the last week with nulls
            while (currentWeek.length < 7) {
                currentWeek.push(null);
            }
            weeks.push({ days: currentWeek, weekIndex: weeks.length });
            currentWeek = [];
        }
    }

    return weeks;
}
